<template>
  <!-- Wrapper to ensure the whole panel is ignored when the user is not logged in -->
  <div
    aria-live="polite"
    aria-label="Notifications area"
  >
    <!-- Trigger + dropdown -->
    <v-menu
      v-show="isLogged"
      v-if="isLogged"
      v-model="notficationsDropDown"
      :close-on-content-click="false"
      :nudge-width="200"
      transition="slide-y-transition"
      bottom
      offset-x
    >
      <!-- Activator: notification bell with badge -->
      <template #activator="{ on, attrs }">
        <v-btn
          icon
          v-bind="attrs"
          :aria-label="$t('components.notifications.tooltip')"
          :aria-expanded="notficationsDropDown"
          aria-haspopup="true"
          @click="notificationsVisibility()"
          v-on="on"
        >
          <v-badge
            color="accent"
            centered
            right
            overlap
            :value="notificationsList.length > 0"
            :aria-label="'Unread {{ notificationsList.length }}'"
          >
            <template
              #badge
              class="font-weight-thin"
            >
              {{
                notificationsList.length || 0
              }}
            </template>
            <v-tooltip bottom>
              <template
                #activator="{ on: tooltipOn, attrs: tooltipAttrs }"
              >
                <v-icon
                  medium
                  color="white"
                  v-bind="tooltipAttrs"
                  aria-hidden="true"
                  v-on="tooltipOn"
                >
                  fal fa-bell
                </v-icon>
              </template>
              <span>{{
                $t("components.notifications.tooltip")
              }}</span>
            </v-tooltip>
          </v-badge>
        </v-btn>
      </template>

      <!-- Dropdown content -->
      <v-card
        role="menu"
        aria-label="Notifications menu"
        width="320"
      >
        <!-- Header -->
        <v-list dense>
          <v-list-item>
            <v-list-item-content class="text-uppercase">
              <v-list-item-title>
                <v-icon
                  size="20"
                  aria-hidden="true"
                >
                  fal fa-bell
                </v-icon>
                <span class="h6 font-weight-thin">{{ $t('components.notifications.title') }}</span>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <v-divider />

        <!-- Loading indicator -->
        <v-progress-linear
          v-show="isLoading"
          color="accent"
          indeterminate
          aria-label="Loading"
        />

        <!-- Empty state -->
        <v-list
          v-if="!notificationsList.length"
          dense
        >
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title class="text-center">
                <v-icon
                  size="18"
                  aria-hidden="true"
                >
                  fal fa-empty-set
                </v-icon>
                <span class="font-weight-thin">{{ $t('components.notifications.contentless') }}</span>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <v-list dense>
          <v-list-item
            v-for="notification in notificationsList"
            :key="notification.id"
            class="notification pb-1 pt-1"
            active-class="notification"
            link
            v-bind="notification.navigateTo"
          >
            <v-list-item-avatar size="25">
              <v-icon
                :color="notification.iconColor"
                aria-hidden="true"
              >
                {{ `fad fa-${notification.icon}` }}
              </v-icon>
            </v-list-item-avatar>

            <v-list-item-content
              v-if="notification.message"
            >
              <v-list-item-title>
                {{
                  notification.message
                }}
              </v-list-item-title>

              <v-list-item-subtitle
                class="text-subtitle-1"
                v-text="toReadableDate(notification.created_at)"
              />
            </v-list-item-content>
            <v-spacer />
            <v-list-item-action>
              <v-icon
                small
                color="white"
                aria-label="Delete"
                tabindex="0"
                role="button"
                @click="markNotification(notification.id)"
                @keydown.enter="markNotification(notification.id)"
                @keydown.space.prevent="markNotification(notification.id)"
              >
                fal fa-trash
              </v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <!-- Clear all -->
        <v-card-actions v-if="notificationsList.length">
          <v-spacer />
          <v-btn
            text
            color="secondary"
            :aria-label="$t('components.notifications.buttons.clear')"
            @click="clearNotifications"
          >
            {{ $t('components.notifications.buttons.clear') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import actions from "@/store/actions";
import { toHumanized } from "@/utils/date.utils";

export default {
    name: "Notifications",
    data() {
        return {
            isLoading: false,
            notficationsDropDown: false,
        }
    },
    computed: {
        ...mapGetters(["notifications", "isLogged"]),

        notificationsList() {
            return this.notifications.filter((notification) => notification.type !== 'GENERAL_NOTIFICATION');
        }
    },
    watch: {
        notificationsList(newValue, oldValue) {
            this.isLoading = this.isLoading && newValue.length == oldValue.length;
        }
    },
    methods: {
        notificationsVisibility() {
            // track event
            // this.$ga.event(
            //     "notifications-list",
            //     "visibility",
            //     String(!this.notficationsDropDown)
            // );
            // return
            this.notficationsDropDown = !this.notficationsDropDown;
        },
        markNotification(id) {
            this.isLoading = true;
            this.$api.markNotification(id).then(notifications => {
                actions.updateNotifications(notifications);
                // return
                return notifications;
            });
        },
        clearNotifications() {
            this.isLoading = true;
            this.$api.clearNotifications().then(notifications => {
                actions.updateNotifications(notifications);
                // return
                return notifications;
            });
        },
        toLink(link) {
            // track event
            // this.$ga.event("notifications-list", "link", link);

            this.$router.push({
                path: new URL(link).pathname
            });
        },
        toReadableDate(date) {
            return toHumanized(date);
        },
    }
};
</script>


<style lang="scss" scoped>
@import '~/_variables.scss';

    .notification {
        border-bottom: 1px solid $tundora;

        &:hover {
            background: $accent;
            color: $white;
        }

        &:active {
            background: $accent;
            color: $white;
        }
    }
</style>
